import { Fragment, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { NodeSelection } from "@tiptap/pm/state";
import { StarterKit } from "@tiptap/starter-kit";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { Link } from "@tiptap/extension-link";
import { common, createLowlight } from "lowlight";
import { TableKit } from "@tiptap/extension-table";
import { StyledTable, StyledTableCell, StyledTableHeader } from "@/components/kbTableExtensions";
import { TableGridPicker, TableToolsMenu } from "@/components/KbTableTools";
import { Highlight } from "@tiptap/extension-highlight";
import {
  BlockLayout, CALLOUT_GROUPS, CALLOUT_LABELS, KbAccordion, KbAttachment, KbAudio, KbCallout, KbColumn, KbColumns,
  KbEmbed, KbEquation, KbHtmlBlock, KbImage, KbMermaid, KbSnippetRef, KbTabPanel, KbTabs, KbToc, KbVideo,
  LINE_HEIGHTS, MERMAID_TEMPLATES,
} from "@/components/kbEditorNodes";
import { KbBlockToolbar } from "@/components/KbBlockToolbar";
import { KbDiagramDialog } from "@/components/KbDiagramDialog";
import { KbHtmlBlockDialog } from "@/components/KbHtmlBlockDialog";
import { dropImages, pasteImages } from "@/lib/kbPasteImages";
import { KbArticleLinkDialog } from "@/components/KbArticleLinkDialog";
import { KbLinkDialog } from "@/components/KbLinkDialog";
import { KbListTools, continueNumbering } from "@/components/KbListTools";
import { KbListAttributes } from "@/components/kbListExtensions";
import { KbMediaLibrary } from "@/components/KbMediaLibrary";
import { KbImageNameField } from "@/components/KbImageNameField";
import { hostAllowed, toEmbedSrc } from "@/lib/kbEmbeds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code, Highlighter, Eraser,
  List, ListOrdered, Quote, Minus, Link2, Link2Off, ImagePlus, ImageDown,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Redo2,
  IndentIncrease, IndentDecrease, Film, FileAudio, FileText, CodeSquare, FolderOpen, Paperclip,
  Info, ChevronsUpDown, Columns2, Sigma, ListTree, Plus, Workflow, Blocks, SquareCode,
  Check, ChevronDown, BookOpen,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const prettyBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const FONTS = [
  { label: "Default", value: "" },
  { label: "Manrope", value: "Manrope, sans-serif" },
  { label: "IBM Plex Sans", value: "'IBM Plex Sans', sans-serif" },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier", value: "'Courier New', monospace" },
];
const SIZES = ["13px", "14px", "16px", "18px", "20px", "24px", "30px"];
const lowlight = createLowlight(common);

// Links to other KB articles carry the target's article id so the reader can
// re-resolve the URL if that article's slug is ever changed.
const KbLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-kb-article": {
        default: null,
        parseHTML: (el) => el.getAttribute("data-kb-article") || null,
        renderHTML: (attrs) => (attrs["data-kb-article"]
          ? { "data-kb-article": attrs["data-kb-article"] } : {}),
      },
      // A link styled as a button — the same call-to-action Helpjuice offered.
      class: {
        default: null,
        parseHTML: (el) => (el.classList.contains("kb-link-button") ? "kb-link-button" : null),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },
});
const CODE_LANGS = ["plaintext", "bash", "json", "xml", "yaml", "javascript", "python", "sql", "ini", "diff"];
const WIDTHS = [["25%", "25%"], ["50%", "50%"], ["75%", "75%"], ["100%", "Full"], [null, "Auto"]];

function Btn({ active, onClick, title, disabled, children }) {
  return (
    <button type="button" title={title} disabled={disabled}
      data-testid={`kb-rte-${title?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors disabled:opacity-40
        ${active ? "bg-primary/15 text-primary" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
      {children}
    </button>
  );
}
const Sep = () => <span className="mx-0.5 h-5 w-px shrink-0 bg-slate-200" />;

/** `tall` — a page-sized writing area (used by the article dialog) that grows
 *  with the content instead of scrolling inside itself, so the modal scrolls. */
export function KbRichEditor({ value, onChange, tall = false }) {
  const fileRef = useRef(null);
  const mediaRef = useRef(null);
  const editorRef = useRef(null);
  const blockEditRef = useRef(null);
  const uploadingRef = useRef(false);
  const [library, setLibrary] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedKind, setEmbedKind] = useState("video");
  const [embedInput, setEmbedInput] = useState("");
  const [embedTitle, setEmbedTitle] = useState("");
  const [embedHeight, setEmbedHeight] = useState("");
  const [hosts, setHosts] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [htmlOpen, setHtmlOpen] = useState(false);
  // The doc often ends with a video, and "selection at the end of the
  // document" resolves to that node — which would pop the block bar open
  // before anyone has touched the editor.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    api.get("/kb/settings").then((r) => setHosts(r.data.embed_hosts || [])).catch(() => {});
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, codeBlock: false }),
      KbListAttributes,
      KbLink.configure({ openOnClick: false, autolink: true }),
      CodeBlockLowlight.configure({ lowlight, defaultLanguage: "plaintext" }),
      BlockLayout, KbEmbed, KbAudio, KbVideo, KbAttachment, KbCallout, KbAccordion, KbTabs, KbTabPanel,
      KbColumns, KbColumn, KbEquation, KbToc, KbMermaid, KbSnippetRef, KbHtmlBlock,
      TextStyle, Color, FontFamily, FontSize,
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      KbImage.configure({ inline: false, allowBase64: false }),
      TableKit.configure({
        // Iter 120 — swap in the styled variants so borders/shading persist.
        table: false, tableCell: false, tableHeader: false,
      }),
      StyledTable.configure({ resizable: true }),
      StyledTableCell, StyledTableHeader,
    ],
    content: value || "",
    // Iter 120 — tiptap v3 doesn't re-render on selection-only transactions by
    // default, so toolbar state (active marks, and the Table panel that only
    // exists when the cursor sits in a table) went stale when you clicked into
    // an existing table.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: { class: "kb-prose kb-editor-body focus:outline-none" },
      // Iter 186 — a pasted picture has to become OUR picture immediately.
      // Left alone, a paste from Helpjuice keeps their URL (which is how
      // thousands of articles ended up hotlinked), a screenshot silently
      // vanishes, and a Word paste embeds a base64 blob in the article.
      handlePaste: (_view, event) => pasteImages(editorRef.current, event),
      handleDrop: (_view, event) => dropImages(editorRef.current, event),
      // Clicking a video/PDF/diagram must SELECT it — ProseMirror leaves the
      // caret in the paragraph next door, so the block toolbar never appeared.
      handleClickOn: (view, _pos, node, nodePos) => {
        if (["kbEmbed", "kbHtmlBlock", "kbMermaid"].includes(node?.type?.name)) {
          view.dispatch(view.state.tr.setSelection(
            NodeSelection.create(view.state.doc, nodePos)));
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        // Selecting an embedded block, done on mousedown so the browser never
        // gets to move the caret — that native selection change was undoing
        // the NodeSelection a moment later, which is why clicking a video
        // appeared to do nothing at all.
        mousedown: (view, event) => {
          setTouched(true);
          const dom = event.target?.closest?.(
            "[data-kb-block='embed'], [data-kb-block='html'], [data-kb-block='mermaid']");
          if (!dom || dom === view.dom || !view.dom.contains(dom)) return false;
          const parent = dom.parentNode;
          const index = Array.prototype.indexOf.call(parent.childNodes, dom);
          let pos;
          try {
            pos = view.posAtDOM(parent, index);
          } catch {
            return false;
          }
          event.preventDefault();
          view.focus();
          view.dispatch(view.state.tr.setSelection(
            NodeSelection.create(view.state.doc, pos)));
          // We swallow the mousedown, so ProseMirror never gets to raise its
          // own double-click event — open the dialog from here instead.
          if (event.detail >= 2) {
            const name = view.state.doc.nodeAt(pos)?.type?.name;
            if (name) blockEditRef.current?.(name);
          }
          return true;
        },
      },
      // A video/PDF/diagram can't be typed into, so a double-click on one is
      // only ever "let me change this" — open its dialog rather than doing
      // nothing, which is what it did before.
      handleDoubleClickOn: (_view, _pos, node) => {
        const name = node?.type?.name;
        if (["kbEmbed", "kbHtmlBlock", "kbMermaid"].includes(name)) {
          blockEditRef.current?.(name);
          return true;
        }
        return false;
      },
      // ⌘K / Ctrl-K is what everyone's fingers already do for a link.
      handleKeyDown: (_view, event) => {
        setTouched(true);
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
          event.preventDefault();
          setLinkOpen(true);
          return true;
        }
        return false;
      },
    },
  });

  // Sync external (programmatic) content changes — e.g. "copy from" — without
  // disturbing the cursor during normal typing (value === current then).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // Iter 275 — replacing the doc while the editor's node is detached (a modal
    // closing, a route change) made ProseMirror's DOM observer flush a mutation
    // against a document that no longer exists: "RangeError: Selection passed
    // to setSelection must point at the current document".
    if (!editor.view?.dom?.isConnected) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop the DOM observer before React detaches the editor, for the same reason.
  useEffect(() => () => {
    const ed = editorRef.current;
    try {
      ed?.view?.dom?.blur();
      ed?.view?.domObserver?.stop?.();
    } catch { /* already gone */ }
  }, []);

  if (!editor) return null;
  editorRef.current = editor;

  // Iter 120 — headings and table header cells are bold from the stylesheet,
  // so stripping the <strong> looks like "the bold button did nothing".
  // Do the toggle, then say why nothing changed.
  const toggleBold = () => {
    const inHeading = editor.isActive("heading");
    const inHeaderCell = editor.isActive("tableHeader");
    editor.chain().focus().toggleBold().run();
    // Explain whenever the click can't have made the text lighter — whether the
    // import left a <strong> inside the heading or not, the stylesheet wins.
    if (!inHeading && !inHeaderCell) return;
    if (inHeading) {
      // Telling someone why it didn't work is only half a fix — offer the fix.
      toast.info("Headings are always bold — that's the stylesheet, not this text.", {
        action: {
          label: "Make it a paragraph",
          onClick: () => editor.chain().focus().setParagraph().run(),
        },
      });
    } else {
      toast.info("Header cells are always bold — that's the stylesheet, not this text.", {
        action: {
          label: "Turn the header row off",
          onClick: () => editor.chain().focus().toggleHeaderRow().run(),
        },
      });
    }
  };

  // Iter 120 — one-click cleanup of the inline fonts/colours/weights the
  // Helpjuice import left behind. Marks only: headings, lists and links stay.
  /** With nothing selected this clears the paragraph (or table cell) the cursor
   *  sits in — imported articles arrive wearing Helpjuice's own colours and
   *  sizes, and selecting every scrap of text before every edit was the whole
   *  annoyance. The cursor goes back where it was. */
  const clearFormatting = () => {
    const { selection } = editor.state;
    const { empty, $from } = selection;
    const cursor = selection.from;
    const chain = editor.chain().focus();
    if (empty) chain.setTextSelection({ from: $from.start(), to: $from.end() });
    ["bold", "italic", "underline", "strike", "code", "highlight", "textStyle"]
      .filter((m) => editor.schema.marks[m])
      .forEach((m) => chain.unsetMark(m));
    if (empty) chain.setTextSelection(cursor);
    chain.run();
    toast.success(empty ? "Formatting cleared from this paragraph" : "Formatting cleared");
  };

  /** A screenshot or a line of prose between steps ends a list; the next one
   *  restarting at 1 is the thing authors keep fixing by hand. Starting a
   *  numbered list right after another one picks up where it left off. */
  const startNumberedList = () => {
    const wasList = editor.isActive("orderedList");
    editor.chain().focus().toggleOrderedList().run();
    if (!wasList) continueNumbering(editor);
  };

  const setLink = () => setLinkOpen(true);
  const selectedText = () => {
    if (!editor) return "";
    const { from, to } = editor.state.selection;
    return from === to ? "" : editor.state.doc.textBetween(from, to, " ").trim();
  };

  /** The words of the link the cursor is sitting in, so editing one keeps its
   *  wording instead of starting blank. */
  const linkText = () => {
    if (!editor || !editor.isActive("link")) return "";
    const { $from } = editor.state.selection;
    const start = $from.start();
    const text = $from.parent.textContent || "";
    let from = $from.pos, to = $from.pos;
    while (from > start && editor.state.doc.rangeHasMark(from - 1, from, editor.schema.marks.link)) from -= 1;
    while (to < start + text.length && editor.state.doc.rangeHasMark(to, to + 1, editor.schema.marks.link)) to += 1;
    return editor.state.doc.textBetween(from, to, " ").trim();
  };

  const applyUrl = (href, opts = {}) => applyLink({ href }, opts.text || href, opts);

  const pickImage = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    const t = toast.loading("Uploading image…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kb/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: data.url }).run();
      toast.success("Image added", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed", { id: t });
    } finally { uploadingRef.current = false; }
  };

  const onMediaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    // The paperclip takes anything; the media buttons keep their own route so a
    // sound file still arrives as a player rather than a download chip.
    const route = e.target.dataset.route === "file" ? "/kb/upload-file" : "/kb/upload-media";
    const t = toast.loading(file.size > 4 * 1024 * 1024 ? "Uploading — large files take a moment…" : "Uploading…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(route, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      insertAsset(data);
      toast.success("Added", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed", { id: t });
    } finally { uploadingRef.current = false; }
  };

  const pickMedia = (accept, route = "media") => {
    if (mediaRef.current) {
      mediaRef.current.accept = accept;
      mediaRef.current.dataset.route = route;
      mediaRef.current.click();
    }
  };

  const insertAsset = (asset) => {
    if (!asset?.url) return;
    // "Replace" on the image toolbar swaps the picture you're standing on
    // instead of adding another one next to it.
    if (replaceMode) {
      setReplaceMode(false);
      editor.chain().focus().updateAttributes("image", { src: asset.url }).run();
      return;
    }
    if (asset.kind === "audio") {
      editor.chain().focus().insertAudio({ src: asset.url, name: asset.filename || "" }).run();
    } else if (asset.kind === "video") {
      editor.chain().focus().insertVideo({ src: asset.url, name: asset.filename || "" }).run();
    } else if (asset.kind === "file") {
      editor.chain().focus().insertAttachment({
        href: asset.url, name: asset.filename || "Attachment",
        size: prettyBytes(asset.size), ext: (asset.filename || "").split(".").pop(),
      }).run();
    } else if (asset.kind === "pdf") {
      editor.chain().focus().insertEmbed({ src: asset.url, kind: "pdf", title: asset.filename || "PDF", height: "720" }).run();
    } else {
      editor.chain().focus().setImage({ src: asset.url, alt: asset.filename || "" }).run();
    }
  };

  const imageByUrl = () => {
    const url = window.prompt("Image URL (https://…)");
    if (url) editor.chain().focus().setImage({ src: url.trim() }).run();
  };

  const applyEmbed = () => {
    const src = toEmbedSrc(embedInput);
    if (!src) return toast.error("Paste a share link or an <iframe> embed snippet");
    if (!hostAllowed(src, hosts)) {
      return toast.error("That host isn't on the KB embed allow-list — a super admin can add it in KB Settings.");
    }
    const attrs = {
      src, kind: embedKind,
      title: embedTitle.trim() || (embedKind === "video" ? "Video" : "Embedded content"),
      height: embedHeight.trim(),
    };
    if (embedAttrs) editor.chain().focus().updateAttributes("kbEmbed", attrs).run();
    else editor.chain().focus().insertEmbed(attrs).run();
    setEmbedInput("");
    setEmbedTitle("");
    setEmbedHeight("");
    setEmbedOpen(false);
  };

  /** Open the embed dialog on whatever is selected, or empty for a new one. */
  const openEmbedDialog = (kind) => {
    const existing = editor.isActive("kbEmbed") ? editor.getAttributes("kbEmbed") : null;
    setEmbedKind(existing?.kind || kind || "video");
    setEmbedInput(existing?.src || "");
    setEmbedTitle(existing?.title || "");
    setEmbedHeight(existing?.height ? String(existing.height) : "");
    setEmbedOpen(true);
  };

  const insertEquation = () => {
    const latex = window.prompt("LaTeX (e.g. E = mc^2 or \\frac{a}{b})");
    if (latex?.trim()) editor.chain().focus().insertEquation(latex.trim()).run();
  };

  const linkArticle = (a, opts = {}) => {
    applyLink({ href: `/kb/a/${a.slug}`, "data-kb-article": a.article_id },
              opts.text || a.name, opts);
  };

  /** Put the link in, using `text` as the words the reader sees: replacing the
   *  selection when they renamed it, wrapping it when they didn't. */
  const applyLink = (attrs, text, opts = {}) => {
    const full = { ...attrs,
                   class: opts.asButton ? "kb-link-button" : null,
                   target: opts.newTab ? "_blank" : null,
                   rel: opts.newTab ? "noopener noreferrer nofollow" : null };
    const { from, to } = editor.state.selection;
    const selected = from === to ? "" : editor.state.doc.textBetween(from, to, " ");
    const chain = editor.chain().focus();
    if (!selected || (text && text !== selected)) {
      chain.insertContent({ type: "text", text: text || attrs.href,
                            marks: [{ type: "link", attrs: full }] }).run();
    } else {
      chain.extendMarkRange("link").setLink(full).run();
    }
  };

  const insertSnippet = (a) => {
    editor.chain().focus().insertSnippetRef({ snippetId: a.article_id, name: a.name }).run();
  };

  const diagramAttrs = editor.isActive("kbMermaid") ? editor.getAttributes("kbMermaid") : null;
  const htmlAttrs = editor.isActive("kbHtmlBlock") ? editor.getAttributes("kbHtmlBlock") : null;
  const embedAttrs = editor.isActive("kbEmbed") ? editor.getAttributes("kbEmbed") : null;

  /** The bar over a selected block, and a double-click on it, both land here. */
  const editBlock = (name) => {
    if (name === "kbEmbed") openEmbedDialog();
    else if (name === "kbHtmlBlock") setHtmlOpen(true);
    else if (name === "kbMermaid") setDiagramOpen(true);
  };
  blockEditRef.current = editBlock;

  const calloutActive = editor.isActive("kbCallout");
  const accordionActive = editor.isActive("kbAccordion");
  const tabActive = editor.isActive("kbTabPanel");
  const imgAttrs = editor.isActive("image") ? editor.getAttributes("image") : null;
  const setImgAttr = (attrs) => editor.chain().updateAttributes("image", attrs).run();

  const headingValue = editor.isActive("heading", { level: 1 }) ? "1"
    : editor.isActive("heading", { level: 2 }) ? "2"
    : editor.isActive("heading", { level: 3 }) ? "3"
    : editor.isActive("heading", { level: 4 }) ? "4" : "p";

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="kb-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 px-2 py-1.5">
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 className="h-4 w-4" /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 className="h-4 w-4" /></Btn>
        <Sep />
        <select data-testid="kb-rte-block" value={headingValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "p") editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: Number(v) }).run();
          }}
          className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-xs text-slate-700 dark:text-slate-200">
          <option value="p">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
        </select>
        <select data-testid="kb-rte-font" onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontFamily(v).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-xs text-slate-700 dark:text-slate-200">
          {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select data-testid="kb-rte-size" defaultValue="" onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(v).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-xs text-slate-700 dark:text-slate-200">
          <option value="">Size</option>
          {SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
        </select>
        <Sep />
        <Btn title="Bold" active={editor.isActive("bold")} onClick={toggleBold}><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>
        <Btn title="Strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
        <Btn title="Highlight" active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()}><Highlighter className="h-4 w-4" /></Btn>
        <Btn title="Clear formatting" onClick={clearFormatting}><Eraser className="h-4 w-4" /></Btn>
        <label className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800" title="Text colour" data-testid="kb-rte-color">
          <span className="h-4 w-4 rounded-full border border-slate-300 dark:border-slate-700" style={{ background: editor.getAttributes("textStyle").color || "#334155" }} />
          <input type="color" className="sr-only" value={editor.getAttributes("textStyle").color || "#334155"}
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()} />
        </label>
        <Sep />
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={startNumberedList}><ListOrdered className="h-4 w-4" /></Btn>
        <KbListTools editor={editor} />
        <Btn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
        <Btn title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
        <Btn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
        <Btn title="Justify" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="h-4 w-4" /></Btn>
        <Btn title="Outdent" onClick={() => (editor.isActive("listItem")
          ? editor.chain().focus().liftListItem("listItem").run()
          : editor.chain().focus().outdentBlock().run())}><IndentDecrease className="h-4 w-4" /></Btn>
        <Btn title="Indent" onClick={() => (editor.isActive("listItem")
          ? editor.chain().focus().sinkListItem("listItem").run()
          : editor.chain().focus().indentBlock().run())}><IndentIncrease className="h-4 w-4" /></Btn>
        <select data-testid="kb-rte-line-height" defaultValue=""
          onChange={(e) => editor.chain().focus().setLineHeight(e.target.value).run()}
          className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-xs text-slate-700 dark:text-slate-200"
          title="Line spacing">
          <option value="">Spacing</option>
          {LINE_HEIGHTS.filter(Boolean).map((h) => <option key={h} value={h}>{h}×</option>)}
        </select>
        <Sep />
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <Btn title="Code block" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code className="h-4 w-4" /></Btn>
        <Btn title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Btn>
        <Btn title="Link — KB article or external URL (⌘K / Ctrl-K)"
          active={editor.isActive("link")} onClick={setLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn title="Remove link" onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")}><Link2Off className="h-4 w-4" /></Btn>
        <Btn title="Insert image" onClick={pickImage}><ImagePlus className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="kb-rte-image-input" />
        <Btn title="Image from a web address (URL)" onClick={imageByUrl}><ImageDown className="h-4 w-4" /></Btn>
        <Btn title="Media library" onClick={() => setLibrary(true)}><FolderOpen className="h-4 w-4" /></Btn>
        <Btn title="Insert or edit a video (embed a link, or upload a short MP4/MOV)" onClick={() => openEmbedDialog("video")}><Film className="h-4 w-4" /></Btn>
        <Btn title="Insert audio" onClick={() => pickMedia("audio/*")}><FileAudio className="h-4 w-4" /></Btn>
        <Btn title="Insert PDF" onClick={() => pickMedia("application/pdf")}><FileText className="h-4 w-4" /></Btn>
        <Btn title="Attach a file" onClick={() => pickMedia("", "file")}><Paperclip className="h-4 w-4" /></Btn>
        <Btn title="Embed code" onClick={() => openEmbedDialog("embed")}><CodeSquare className="h-4 w-4" /></Btn>
        <input ref={mediaRef} type="file" accept="audio/*,application/pdf" className="hidden"
          onChange={onMediaFile} data-testid="kb-rte-media-input" />
        <Sep />
        <a href="/kb/a/house-style-callouts-and-blocks" target="_blank" rel="noreferrer"
          title="House style — which callout to use when" data-testid="kb-rte-house-style"
          className="ml-auto flex h-8 items-center gap-1 rounded px-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary">
          <BookOpen className="h-3.5 w-3.5" /> House style
        </a>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" title="Callout box" data-testid="kb-rte-callout-menu"
              className="flex h-8 items-center gap-0.5 rounded px-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Info className="h-4 w-4" /><ChevronDown className="h-3 w-3 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56" data-testid="kb-rte-callout-menu-content">
            {CALLOUT_GROUPS.map(([group, ids], gi) => (
              <Fragment key={group}>
                {gi > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{group}</DropdownMenuLabel>
                {ids.map((v) => (
                  <DropdownMenuItem key={v} data-testid={`kb-rte-callout-pick-${v}`}
                    onClick={() => (calloutActive
                      ? editor.chain().focus().setCalloutVariant(v).run()
                      : editor.chain().focus().insertCallout(v).run())}>
                    <span className={`mr-2 inline-block h-3 w-3 shrink-0 rounded-full kb-swatch-${v}`} />
                    {CALLOUT_LABELS[v]}
                    {calloutActive && editor.getAttributes("kbCallout").variant === v && (
                      <Check className="ml-auto h-3.5 w-3.5" />
                    )}
                  </DropdownMenuItem>
                ))}
              </Fragment>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="kb-rte-callout-pick-pair"
              onClick={() => editor.chain().focus().insertCalloutPair().run()}>
              <Columns2 className="mr-2 h-3.5 w-3.5" /> Do / Don&apos;t side by side
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Btn title="Accordion (collapsible section)" onClick={() => editor.chain().focus().insertAccordion().run()}><ChevronsUpDown className="h-4 w-4" /></Btn>
        <Btn title="Tabs" onClick={() => editor.chain().focus().insertTabs().run()}><FolderOpen className="hidden" /><ListTree className="h-4 w-4" /></Btn>
        <Btn title="Two columns" onClick={() => editor.chain().focus().insertColumns(2).run()}><Columns2 className="h-4 w-4" /></Btn>
        <Btn title="Table of contents" onClick={() => editor.chain().focus().insertToc().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Equation (LaTeX)" onClick={insertEquation}><Sigma className="h-4 w-4" /></Btn>
        <Btn title="Insert reusable block (snippet)" onClick={() => setSnippetOpen(true)}><Blocks className="h-4 w-4" /></Btn>
        <Btn title={diagramAttrs ? "Edit diagram" : "Diagram (Mermaid)"} active={!!diagramAttrs}
          onClick={() => setDiagramOpen(true)}><Workflow className="h-4 w-4" /></Btn>
        <Btn title={htmlAttrs ? "Edit interactive block" : "Interactive block (HTML/JS, sandboxed)"}
          active={!!htmlAttrs} onClick={() => setHtmlOpen(true)}><SquareCode className="h-4 w-4" /></Btn>
        <Sep />
        <TableGridPicker editor={editor} />
        {editor.isActive("table") && <TableToolsMenu editor={editor} />}
        {editor.isActive("codeBlock") && (
          <select data-testid="kb-rte-code-lang"
            value={editor.getAttributes("codeBlock").language || "plaintext"}
            onChange={(e) => editor.chain().focus().updateAttributes("codeBlock", { language: e.target.value }).run()}
            className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1.5 text-xs text-slate-700 dark:text-slate-200">
            {CODE_LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
      </div>

      {(calloutActive || accordionActive || tabActive) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-3 py-2"
          data-testid="kb-rte-block-panel">
          {calloutActive && (
            <>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Callout</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="kb-rte-callout-type"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-0.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full kb-swatch-${editor.getAttributes("kbCallout").variant || "note"}`} />
                    {CALLOUT_LABELS[editor.getAttributes("kbCallout").variant] || "Note"}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {CALLOUT_GROUPS.map(([group, ids], gi) => (
                    <Fragment key={group}>
                      {gi > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{group}</DropdownMenuLabel>
                      {ids.map((v) => (
                        <DropdownMenuItem key={v} data-testid={`kb-rte-callout-${v}`}
                          onClick={() => editor.chain().focus().setCalloutVariant(v).run()}>
                          <span className={`mr-2 inline-block h-3 w-3 shrink-0 rounded-full kb-swatch-${v}`} />
                          {CALLOUT_LABELS[v]}
                          {editor.getAttributes("kbCallout").variant === v && <Check className="ml-auto h-3.5 w-3.5" />}
                        </DropdownMenuItem>
                      ))}
                    </Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Input data-testid="kb-rte-callout-title" className="h-7 max-w-[240px] text-xs"
                value={editor.getAttributes("kbCallout").title || ""}
                placeholder="Optional heading, e.g. Before you start"
                onChange={(e) => editor.chain().setCalloutTitle(e.target.value).run()} />
            </>
          )}
          {accordionActive && (
            <>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Accordion</span>
              <Input data-testid="kb-rte-accordion-title" className="h-7 max-w-[280px] text-xs"
                value={editor.getAttributes("kbAccordion").title || ""}
                placeholder="Section title readers click to expand"
                onChange={(e) => editor.chain().updateAttributes("kbAccordion", { title: e.target.value }).run()} />
              <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                <input type="checkbox" className="h-3.5 w-3.5 accent-primary" data-testid="kb-rte-accordion-open"
                  checked={!!editor.getAttributes("kbAccordion").open}
                  onChange={(e) => editor.chain().updateAttributes("kbAccordion", { open: e.target.checked }).run()} />
                Open by default
              </label>
              <button type="button" data-testid="kb-rte-accordion-unwrap"
                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/40"
                title="Remove the accordion but keep its content"
                onClick={() => editor.chain().focus().unwrapAccordion().run()}>
                Remove accordion
              </button>
            </>
          )}
          {tabActive && (
            <>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Tab</span>
              <Input data-testid="kb-rte-tab-title" className="h-7 max-w-[220px] text-xs"
                value={editor.getAttributes("kbTabPanel").title || ""}
                placeholder="Tab label"
                onChange={(e) => editor.chain().updateAttributes("kbTabPanel", { title: e.target.value }).run()} />
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                data-testid="kb-rte-tab-add" onClick={() => editor.chain().focus().addTabPanel().run()}>
                <Plus className="mr-1 h-3 w-3" /> Add tab
              </Button>
            </>
          )}
        </div>
      )}

      {imgAttrs && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-primary/5 px-3 py-2"
          data-testid="kb-rte-image-panel">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">Image</span>
          <button type="button" data-testid="kb-rte-image-replace"
            title="Swap this picture for another one — from the library, or upload a new one"
            onClick={() => { setReplaceMode(true); setLibrary(true); }}
            className="flex items-center gap-1 rounded border border-primary/40 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10 dark:bg-slate-900">
            <ImageDown className="h-3 w-3" /> Replace
          </button>
          <Input data-testid="kb-rte-image-alt" value={imgAttrs.alt || ""} placeholder="Alt text (accessibility & search)"
            onChange={(e) => setImgAttr({ alt: e.target.value })} className="h-7 max-w-[240px] text-xs" />
          <Input data-testid="kb-rte-image-caption" value={imgAttrs.caption || ""} placeholder="Caption (shown under the image)"
            onChange={(e) => setImgAttr({ caption: e.target.value })} className="h-7 max-w-[240px] text-xs" />
          <KbImageNameField src={imgAttrs.src || ""} />
          <span className="flex items-center gap-1">
            {[["left", "Left"], [null, "Centre"], ["right", "Right"]].map(([a, lbl]) => (
              <button key={lbl} type="button" data-testid={`kb-rte-image-align-${lbl.toLowerCase()}`}
                title={`Put the image on the ${lbl.toLowerCase()} of its own line`}
                onClick={() => setImgAttr({ align: a })}
                className={`rounded border px-1.5 py-0.5 text-[11px] ${
                  (imgAttrs.align || null) === a
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800"}`}>
                {lbl}
              </button>
            ))}
            <button type="button" data-testid="kb-rte-image-wrap"
              disabled={!imgAttrs.align}
              title={imgAttrs.align
                ? "Let the text run beside the image instead of below it"
                : "Align the image left or right first, then text can sit beside it"}
              onClick={() => setImgAttr({ wrap: imgAttrs.wrap ? null : true })}
              className={`rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40 ${
                imgAttrs.wrap
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800"}`}>
              Wrap text
            </button>
            {imgAttrs.wrap && [["narrow", "Narrow"], ["half", "Half"], ["wide", "Wide"]].map(([w, lbl]) => (
              <button key={w} type="button" data-testid={`kb-rte-image-wrapwidth-${w}`}
                title={`Let the picture take ${lbl.toLowerCase() === "narrow" ? "a third" : lbl.toLowerCase() === "half" ? "half" : "two thirds"} of the line`}
                onClick={() => setImgAttr({ wrapWidth: imgAttrs.wrapWidth === w ? null : w })}
                className={`rounded border px-1.5 py-0.5 text-[11px] ${
                  imgAttrs.wrapWidth === w
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800"}`}>
                {lbl}
              </button>
            ))}
          </span>
          <span className="flex items-center gap-1">
            {WIDTHS.map(([w, lbl]) => (
              <button key={lbl} type="button" data-testid={`kb-rte-image-width-${lbl}`}
                onClick={() => setImgAttr({ width: w })}
                className={`rounded border px-1.5 py-0.5 text-[11px] ${
                  (imgAttrs.width || null) === w ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-white"}`}>
                {lbl}
              </button>
            ))}
          </span>
        </div>
      )}

      <KbBlockToolbar editor={editor} onEdit={editBlock} enabled={touched} />
      <EditorContent editor={editor} data-testid="kb-rte-content"
        className={tall ? "kb-rte-tall px-4 py-3" : "max-h-[52vh] overflow-y-auto px-4 py-3"} />

      <KbLinkDialog open={linkOpen} onOpenChange={setLinkOpen}
        initialText={linkOpen ? (selectedText() || linkText()) : ""}
        currentHref={editor.getAttributes("link").href || ""}
        currentNewTab={editor.getAttributes("link").target === "_blank"}
        currentButton={editor.getAttributes("link").class === "kb-link-button"}
        onPickArticle={linkArticle} onPickUrl={applyUrl}
        onRemove={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()} />

      <KbArticleLinkDialog open={snippetOpen} onOpenChange={setSnippetOpen} onPick={insertSnippet}
        snippetsOnly title="Insert a reusable block" />

      <KbDiagramDialog open={diagramOpen} onOpenChange={setDiagramOpen}
        templates={MERMAID_TEMPLATES} initial={diagramAttrs}
        onSave={({ code, caption }) => {
          if (diagramAttrs) editor.chain().focus().updateDiagram({ code, caption }).run();
          else editor.chain().focus().insertDiagram(code, caption).run();
        }} />

      <KbHtmlBlockDialog open={htmlOpen} onOpenChange={setHtmlOpen} initial={htmlAttrs}
        onSave={(attrs) => {
          if (htmlAttrs) editor.chain().focus().updateHtmlBlock(attrs).run();
          else editor.chain().focus().insertHtmlBlock(attrs).run();
        }} />

      <KbMediaLibrary open={library} kind={replaceMode ? "image" : ""}
        onOpenChange={(v) => { setLibrary(v); if (!v) setReplaceMode(false); }}
        onPick={insertAsset} />

      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="max-w-lg" data-testid="kb-rte-embed-dialog">
          <DialogHeader>
            <DialogTitle>
              {embedAttrs ? (embedKind === "video" ? "Edit video" : "Edit embed")
                : (embedKind === "video" ? "Insert video" : "Insert embed")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Paste a share link (YouTube, Vimeo, Loom, Synthesia, Scribe…) or a full
            &lt;iframe&gt; embed snippet. Only hosts on the KB allow-list are accepted.
          </p>
          <textarea data-testid="kb-rte-embed-input" rows={3} value={embedInput}
            onChange={(e) => setEmbedInput(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…  or  <iframe src=…></iframe>"
            className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Title (read out by screen readers)
              <Input data-testid="kb-rte-embed-title" value={embedTitle} className="mt-1 h-8 text-xs"
                placeholder={embedKind === "video" ? "Video" : "Embedded content"}
                onChange={(e) => setEmbedTitle(e.target.value)} />
            </label>
            <label className="w-32 text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Height (px)
              <Input data-testid="kb-rte-embed-height" value={embedHeight} className="mt-1 h-8 text-xs"
                placeholder={embedKind === "pdf" ? "720" : "auto (16:9)"}
                onChange={(e) => setEmbedHeight(e.target.value.replace(/[^\d]/g, ""))} />
            </label>
          </div>
          <details className="text-[11px] text-slate-400 dark:text-slate-500">
            <summary className="cursor-pointer">Allowed hosts ({hosts.length})</summary>
            <div className="mt-1 leading-relaxed">{hosts.join(", ")}</div>
          </details>
          {embedKind === "video" && !embedAttrs && (
            <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-3">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-200">…or host it here</p>
              <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                Upload a short MP4, MOV or WebM (up to 50 MB) and it plays inline, staff-only,
                with no third-party host involved. A ProPres or HEVC .mov won&apos;t play in
                Chrome — export as H.264 if the player stays blank.
              </p>
              <Button variant="outline" size="sm" className="mt-2 h-7 text-xs"
                data-testid="kb-rte-video-upload"
                onClick={() => { setEmbedOpen(false); pickMedia("video/*,.mov,.mp4,.webm"); }}>
                <Film className="mr-1 h-3.5 w-3.5" /> Choose a video file
              </Button>
            </div>
          )}
          <DialogFooter className="sm:justify-between">
            {embedAttrs ? (
              <Button variant="outline" data-testid="kb-rte-embed-remove"
                className="text-rose-600 hover:text-rose-700"
                onClick={() => {
                  editor.chain().focus().deleteSelection().run();
                  setEmbedOpen(false);
                }}>
                Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEmbedOpen(false)}>Cancel</Button>
              <Button data-testid="kb-rte-embed-insert" onClick={applyEmbed}>
                {embedAttrs ? "Update" : "Insert"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
